import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import { useTranslation } from "react-i18next";

import Checkbox from "Components/Form/Checkbox";
import { Checklist } from "Models/checklist";
import {
  ReportsSharingConfig,
  ReportsSharingSelectedItem,
  ReportsSharingSource,
} from "Models/company.config";
import { ReportGroup } from "Models/reports.groups";
import { ReportGroup as ReportGroupEnum, ReportSubGroup } from "Util/enums";

import {
  formatReportsSharingTaskTypes,
  getReportsSharingEligibility,
} from "./reportsSharingEligibility";

export type ReportsSharingCatalogItem = ReportsSharingSelectedItem & {
  key: string;
};

export const DEFAULT_REPORTS_SHARING_SELECTED_ITEMS: ReportsSharingSelectedItem[] = [
  {
    source: "report",
    name: "Inventory Condition Report",
  },
];

export const getDefaultReportsSharingConfig = (): ReportsSharingConfig => ({
  selectedItems: DEFAULT_REPORTS_SHARING_SELECTED_ITEMS.map((item) => ({ ...item })),
});

const normalizeName = (name?: string) => (name || "").trim().toLowerCase();

const isActiveStatus = (status?: string) => !status || status.toLowerCase() === "active";

const hasPositiveSourceId = (sourceId?: number) =>
  typeof sourceId === "number" && Number.isFinite(sourceId) && sourceId > 0;

const normalizeSourceId = (sourceId?: number) => {
  const numericSourceId = Number(sourceId);

  return Number.isFinite(numericSourceId) && numericSourceId > 0
    ? numericSourceId
    : undefined;
};

const buildItemIdentity = (source: ReportsSharingSource, name: string, sourceId?: number) =>
  `${source}:${sourceId || normalizeName(name)}`;

const toSelectedItem = (
  item: Omit<ReportsSharingSelectedItem, "sourceId"> & { sourceId?: number },
): ReportsSharingSelectedItem => {
  const selectedItem: ReportsSharingSelectedItem = {
    source: item.source,
    name: item.name.trim(),
  };
  const sourceId = normalizeSourceId(item.sourceId);

  if (sourceId) {
    selectedItem.sourceId = sourceId;
  }

  return selectedItem;
};

const areReportsSharingItemsEqual = (
  selectedItem: ReportsSharingSelectedItem,
  catalogItem: ReportsSharingSelectedItem,
) => {
  if (selectedItem.source !== catalogItem.source) return false;

  if (hasPositiveSourceId(selectedItem.sourceId) && hasPositiveSourceId(catalogItem.sourceId)) {
    return selectedItem.sourceId === catalogItem.sourceId;
  }

  return normalizeName(selectedItem.name) === normalizeName(catalogItem.name);
};

export const normalizeReportsSharingSelectedItems = (
  selectedItems?: ReportsSharingSelectedItem[] | null,
): ReportsSharingSelectedItem[] => {
  if (!Array.isArray(selectedItems)) {
    return DEFAULT_REPORTS_SHARING_SELECTED_ITEMS.map((item) => ({ ...item }));
  }

  const seen = new Set<string>();

  return selectedItems.reduce<ReportsSharingSelectedItem[]>((acc, item) => {
    if (!item || (item.source !== "report" && item.source !== "checklist")) {
      return acc;
    }

    const name = typeof item.name === "string" ? item.name.trim() : "";

    if (!name) return acc;

    const normalizedItem = toSelectedItem({
      source: item.source,
      sourceId: item.sourceId,
      name,
    });
    const key = buildItemIdentity(
      normalizedItem.source,
      normalizedItem.name,
      normalizedItem.sourceId,
    );

    if (seen.has(key)) return acc;

    seen.add(key);
    acc.push(normalizedItem);

    return acc;
  }, []);
};

export const buildOperationsOrdersReportsSharingCatalog = (
  reportGroups?: ReportGroup[],
  checklists?: Checklist[],
): ReportsSharingCatalogItem[] => {
  const seen = new Set<string>();
  const items: ReportsSharingCatalogItem[] = [];

  const addItem = (source: ReportsSharingSource, name?: string, sourceId?: number) => {
    const normalizedName = typeof name === "string" ? name.trim() : "";

    if (!normalizedName) return;

    const normalizedSourceId = normalizeSourceId(sourceId);
    const identity = buildItemIdentity(source, normalizedName, normalizedSourceId);

    if (seen.has(identity)) return;

    seen.add(identity);
    items.push({
      source,
      sourceId: normalizedSourceId,
      name: normalizedName,
      key: identity,
    });
  };

  reportGroups
    ?.filter(
      (reportGroup) =>
        reportGroup?.group === ReportGroupEnum.Operations &&
        reportGroup?.subGroup === ReportSubGroup.Orders,
    )
    .forEach((reportGroup) => {
      reportGroup.reports?.forEach((report) => {
        if (report?.valid === false || !isActiveStatus(report?.status)) return;
        if (!report?.description) return;

        addItem("report", report?.name, report?.id);
      });
    });

  checklists?.forEach((checklist) => {
    if (checklist?.valid === false || !isActiveStatus(checklist?.status)) return;

    addItem("checklist", checklist?.description || checklist?.code, checklist?.id);
  });

  return items;
};

export const isReportsSharingItemSelected = (
  selectedItems: ReportsSharingSelectedItem[] | undefined,
  catalogItem: ReportsSharingSelectedItem,
) => {
  return normalizeReportsSharingSelectedItems(selectedItems).some((selectedItem) =>
    areReportsSharingItemsEqual(selectedItem, catalogItem),
  );
};

export const toggleReportsSharingSelectedItem = (
  selectedItems: ReportsSharingSelectedItem[] | undefined,
  catalogItem: ReportsSharingSelectedItem,
  checked: boolean,
) => {
  const normalizedSelectedItems = normalizeReportsSharingSelectedItems(selectedItems);

  if (!checked) {
    return normalizedSelectedItems.filter(
      (selectedItem) => !areReportsSharingItemsEqual(selectedItem, catalogItem),
    );
  }

  if (isReportsSharingItemSelected(normalizedSelectedItems, catalogItem)) {
    return normalizedSelectedItems;
  }

  return [...normalizedSelectedItems, toSelectedItem(catalogItem)];
};

type ReportsSharingConfigDetailsProps = {
  catalogItems: ReportsSharingCatalogItem[];
  selectedItems?: ReportsSharingSelectedItem[];
  disabled?: boolean;
  isLoading?: boolean;
  onToggle: (item: ReportsSharingCatalogItem, checked: boolean) => void;
};

export const ReportsSharingConfigDetails = ({
  catalogItems,
  selectedItems,
  disabled,
  isLoading,
  onToggle,
}: ReportsSharingConfigDetailsProps) => {
  const { t: translate } = useTranslation();

  if (isLoading) {
    return (
      <Box display="flex" flexDirection="column" gap="12px">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} variant="rounded" height={36} />
        ))}
      </Box>
    );
  }

  if (!catalogItems.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {translate("noReportsAvailableForSharingViaOpsApp")}
      </Typography>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap="12px">
      <Typography color="text.secondary" variant="body2">
        {translate("opsAppReportsSharingEligibilityHint")}
      </Typography>

      <Box display="flex" flexDirection="column" gap="8px">
        {catalogItems.map((item) => {
          const checked = isReportsSharingItemSelected(selectedItems, item);
          const eligibility = getReportsSharingEligibility(item.source, item.name);
          const eligibleTaskTypes = formatReportsSharingTaskTypes(eligibility, translate);

          return (
            <Box
              key={item.key}
              border={`1px solid ${colors.gray200}`}
              borderRadius="8px"
              display="flex"
              flexDirection="column"
              gap="4px"
              padding="8px 12px"
            >
              <Box alignItems="flex-start" display="flex" gap="12px" justifyContent="space-between">
                <Checkbox
                  checked={checked}
                  disabled={disabled}
                  label={item.name}
                  onChange={(_, nextChecked) => onToggle(item, nextChecked)}
                />
                <Typography color="text.secondary" sx={{ flexShrink: 0, pt: "2px" }} variant="caption">
                  {item.source === "report" ? translate("report") : translate("checklist")}
                </Typography>
              </Box>

              <Box display="flex" flexDirection="column" gap="2px" paddingLeft="28px">
                <Typography color="text.secondary" variant="caption">
                  <strong>{translate("opsAppReportsSharingEligibleTaskTypes")}:</strong>{" "}
                  {eligibleTaskTypes}
                </Typography>
                <Typography color="text.secondary" variant="caption">
                  <strong>{translate("opsAppReportsSharingRequirement")}:</strong>{" "}
                  {translate(eligibility.requirementKey)}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
